import { lazy, Suspense, useEffect, useState } from 'react';
import { AnalyticsConsent } from './components/AnalyticsConsent';
import { Footer } from './components/Footer';
import { MotionRoot } from './components/MotionRoot';
import { Navbar } from './components/Navbar';
import { OfflineBanner } from './components/OfflineBanner';
import { SetupScreen } from './components/SetupScreen';
import { ScoreboardX01 } from './components/ScoreboardX01';
import { ScoreboardCricket } from './components/ScoreboardCricket';
import { InputKeypadPerDart } from './components/InputKeypadPerDart';
import { InputKeypadTurnTotal } from './components/InputKeypadTurnTotal';
import { LegWinModal } from './components/LegWinModal';
import { CheckoutPrompt } from './components/CheckoutPrompt';
import { TurnBanner } from './components/TurnBanner';
import { ToastHost } from './components/ToastHost';
import { UpdateToast } from './components/UpdateToast';
import { UpdateProvider } from './hooks/useAppUpdate';
import { AppThemeProvider, useAppTheme } from './hooks/useAppTheme';
import { LanguageProvider, useT } from './hooks/useT';
import { usePreferences, type Preferences } from './hooks/usePreferences';
import { useHistory } from './hooks/useHistory';
import { useMatch } from './hooks/useMatch';
import { useToast } from './hooks/useToast';
import { useWakeLock } from './hooks/useWakeLock';
import { trackPage } from './utils/analytics';

// Dialogen worden pas geladen als ze open gaan.
const SettingsMenu = lazy(() => import('./components/SettingsMenu').then(m => ({ default: m.SettingsMenu })));
const HistoryModal = lazy(() => import('./components/HistoryModal').then(m => ({ default: m.HistoryModal })));
const StatsModal = lazy(() => import('./components/StatsModal').then(m => ({ default: m.StatsModal })));

export default function App() {
  const preferences = usePreferences();
  return (
    <MotionRoot>
      <LanguageProvider language={preferences.prefs.language}>
        <AppThemeProvider themeColor={preferences.prefs.themeColor} isDark={preferences.isDark}>
          <UpdateProvider>
            <Screen preferences={preferences} />
          </UpdateProvider>
        </AppThemeProvider>
      </LanguageProvider>
    </MotionRoot>
  );
}

type Panel = 'menu' | 'history' | 'stats' | null;

function Screen({ preferences }: { preferences: Preferences }) {
  const { t } = useT();
  const { theme } = useAppTheme();
  const { toast, showToast, dismissToast } = useToast();
  const history = useHistory();
  const game = useMatch({ language: preferences.prefs.language, onHistoryChange: history.refresh });
  const [panel, setPanel] = useState<Panel>(null);
  const { gameState } = game;

  useWakeLock(!!gameState && !gameState.isMatchOver);

  // Schermwissels (geen router) tellen als paginaweergave in Google Analytics.
  const view = panel ?? (gameState ? 'spel' : 'start');
  useEffect(() => {
    const titles = { menu: 'Menu', history: 'Geschiedenis', stats: 'Statistieken', spel: 'Spel', start: 'Nieuw spel' } as const;
    trackPage(view === 'start' ? '/' : `/${view}`, titles[view]);
  }, [view]);

  // Stoppen midden in een partij kan per ongeluk: bied herstellen aan.
  const stopGame = () => {
    const wasPlaying = !!gameState && !gameState.isMatchOver && game.match!.events.length > 0;
    const previous = game.stop();
    if (wasPlaying && previous) showToast(t('gameStopped'), 6000, { label: t('undo'), onClick: () => game.resume(previous) });
  };

  const deleteRecord = (id: string) => {
    const restore = history.remove(id);
    showToast(t('recordDeleted'), 6000, { label: t('undo'), onClick: restore });
  };

  const clearHistory = () => {
    const restore = history.clear();
    showToast(t('historyCleared'), 6000, { label: t('undo'), onClick: restore });
  };

  return (
    <div className={`flex min-h-screen flex-col ${theme.appBg} ${theme.appText}`}>
      <OfflineBanner />
      <AnalyticsConsent />
      <UpdateToast />
      <ToastHost toast={toast} onDismiss={dismissToast} />

      <Navbar
        onOpenMenu={() => setPanel('menu')}
        onOpenHistory={() => setPanel('history')}
        onOpenStats={() => setPanel('stats')}
        onNewGame={stopGame}
        isInGame={!!gameState}
      />

      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-2.5 py-2 pb-24 sm:px-4">
        {!gameState ? (
          <SetupScreen onStartGame={game.start} />
        ) : (
          <div className="w-full space-y-2">
            {gameState.config.gameMode === 'cricket' ? (
              <ScoreboardCricket gameState={gameState} onUndo={game.undoLast} canUndo={game.canUndo} />
            ) : (
              <ScoreboardX01
                gameState={gameState}
                onSwitchInputMethod={game.switchInputMethod}
                canSwitchInput={game.canSwitchInput}
                onUndo={game.undoLast}
                canUndo={game.canUndo}
                bustEvent={game.bustEvent}
              />
            )}

            <div className="relative w-full">
              {game.inputMethod === 'per-dart' ? (
                <InputKeypadPerDart
                  onAddDart={game.throwDart}
                  onRemoveLastDart={game.undoLast}
                  currentTurnDarts={gameState.currentTurnDarts}
                  isCricket={gameState.config.gameMode === 'cricket'}
                />
              ) : (
                <InputKeypadTurnTotal onSubmitTotal={game.submitTurnTotal} />
              )}

              {game.turnBanner && !game.checkoutPrompt && (
                <TurnBanner banner={game.turnBanner} isCountUp={gameState.config.gameMode === 'countup'} />
              )}
            </div>
          </div>
        )}
      </main>

      <Footer />

      {game.checkoutPrompt && <CheckoutPrompt request={game.checkoutPrompt} onConfirm={game.confirmCheckout} onCancel={game.cancelCheckout} />}

      {gameState?.isLegOver && <LegWinModal gameState={gameState} onNextLeg={game.nextLeg} onUndo={game.undoLast} onNewGame={stopGame} />}

      <Suspense fallback={null}>
        {panel === 'menu' && <SettingsMenu preferences={preferences} onClose={() => setPanel(null)} />}
        {panel === 'history' && (
          <HistoryModal history={history.history} onClose={() => setPanel(null)} onDeleteRecord={deleteRecord} onClearHistory={clearHistory} />
        )}
        {panel === 'stats' && <StatsModal history={history.history} onClose={() => setPanel(null)} />}
      </Suspense>
    </div>
  );
}
